/**
 * Wave V1 — WhatsApp bridge helpers.
 *
 * Pure, side-effect free. No React, no server imports; safe from any bundle.
 *
 * Bangladesh numbering:
 *   - Local mobile numbers are 11 digits starting with `01` (e.g. 01811600591).
 *   - The country code is +880. `wa.me` expects the number WITHOUT the `+`
 *     and WITHOUT any spaces / dashes / brackets, e.g. `8801811600591`.
 *   - Shop rows in the DB may store any of: `01…`, `+8801…`, `8801…`,
 *     `+88 018 …`, `018-11…`. We normalise everything to `+8801XXXXXXXXX`.
 */

const RE_STRIP = /[^\d+]/g;

/**
 * Normalise a raw Bangladesh phone/WhatsApp number to E.164 (`+8801XXXXXXXXX`).
 * Returns `null` when the input clearly isn't a BD mobile number so callers
 * can hide the WhatsApp CTA instead of building a broken `wa.me` link.
 *
 * Accepted shapes (all → +8801811600591):
 *   01811600591, 01811-600591, +8801811600591, 8801811600591,
 *   +88 018 11 60 05 91, 018 116 00 591
 * Rejected:
 *   empty, non-mobile prefixes (02…, 09…), too short / too long.
 */
export function normalizeBdPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = String(raw).replace(RE_STRIP, "");

  // Drop a leading '+' now that non-digits are gone.
  if (s.startsWith("+")) s = s.slice(1);

  // '00' international prefix → drop.
  if (s.startsWith("00")) s = s.slice(2);

  // Local `01XXXXXXXXX` → prepend country code.
  // Local `01XXXXXXXXX` → drop the leading 0 and prepend country code so we
  // end up with `8801XXXXXXXXX` (not `88001…`).
  if (s.startsWith("01") && s.length === 11) s = "880" + s.slice(1);

  // Now expect `880` + 10 digits, where the subscriber part starts with `1`
  // (mobile numbers only — landlines / short codes aren't on WhatsApp).
  if (!/^8801\d{9}$/.test(s)) return null;

  return "+" + s;
}

/** Same as `normalizeBdPhone` but returns the `wa.me`-shaped bare digits. */
export function toWaMeDigits(raw: string | null | undefined): string | null {
  const e164 = normalizeBdPhone(raw);
  return e164 ? e164.slice(1) : null;
}

export type BuildWaLinkInput = {
  phone: string | null | undefined;
  /** Falls back to `phone` when null. Lets shops publish a WA-only number. */
  whatsappNumber?: string | null;
  message?: string;
};

/**
 * Build a `https://wa.me/<digits>?text=<url-encoded>` link.
 * Returns `null` when no usable phone is available so callers can gate the CTA.
 */
export function buildWaLink({ phone, whatsappNumber, message }: BuildWaLinkInput): string | null {
  const digits = toWaMeDigits(whatsappNumber ?? phone);
  if (!digits) return null;
  const base = `https://wa.me/${digits}`;
  if (!message) return base;
  return `${base}?text=${encodeURIComponent(message.slice(0, 1000))}`;
}

/**
 * Optional vehicle details the buyer types before opening the chat, so the
 * seller doesn't have to open with "which vehicle?". Every field is optional.
 */
export type Fitment = {
  vehicle?: string | null;
  year?: string | null;
  chassis?: string | null;
};

/** Trim + cap a fitment field so a paste can't blow the wa.me text limit. */
function fitField(v: string | null | undefined, max = 40): string {
  return String(v ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

/** True when the buyer typed at least one vehicle detail. */
export function hasFitment(f: Fitment | null | undefined): boolean {
  if (!f) return false;
  return Boolean(fitField(f.vehicle) || fitField(f.year) || fitField(f.chassis));
}

/**
 * Render the vehicle block appended to a WhatsApp message.
 * Returns "" when nothing was filled in, so the message is unchanged.
 */
export function formatFitment(f: Fitment | null | undefined, lang: "bn" | "en" = "bn"): string {
  if (!hasFitment(f)) return "";
  const vehicle = fitField(f?.vehicle);
  const year = fitField(f?.year, 8);
  const chassis = fitField(f?.chassis, 32);
  const parts: string[] = [];
  if (lang === "bn") {
    if (vehicle) parts.push(`গাড়ি: ${vehicle}`);
    if (year) parts.push(`সাল: ${year}`);
    if (chassis) parts.push(`ইঞ্জিন/চেসিস: ${chassis}`);
  } else {
    if (vehicle) parts.push(`Vehicle: ${vehicle}`);
    if (year) parts.push(`Year: ${year}`);
    if (chassis) parts.push(`Engine/chassis: ${chassis}`);
  }
  return "\n" + parts.join("\n");
}

export type ProductShareContext = {
  titleEn: string;
  titleBn?: string | null;
  price: number;
  currency?: string; // default "৳"
  url: string;
  shopName?: string | null;
  /** Buyer's vehicle, when they filled it in on the product page. */
  fitment?: Fitment | null;
};

/**
 * Bilingual pre-filled buyer message. Kept short so it survives the
 * ~1000-char `text` limit on the wa.me deeplink even with long titles.
 * Bengali first — most buyers in the parts market message in Bangla.
 */
export function buildProductShareMessage(ctx: ProductShareContext): string {
  const cur = ctx.currency ?? "৳";
  const price = Number.isFinite(ctx.price) ? `${cur}${Math.round(ctx.price).toLocaleString("en-BD")}` : "";
  const shop = ctx.shopName ? `\n${ctx.shopName}` : "";
  const fitBn = formatFitment(ctx.fitment, "bn");
  const fitEn = formatFitment(ctx.fitment, "en");

  const bn = ctx.titleBn
    ? `হ্যালো! এই পার্টটি এখনও স্টকে আছে?\n${ctx.titleBn}${price ? ` — ${price}` : ""}${shop}${fitBn}\n${ctx.url}`
    : `হ্যালো! এই পার্টটি এখনও স্টকে আছে?\n${ctx.titleEn}${price ? ` — ${price}` : ""}${shop}${fitBn}\n${ctx.url}`;

  // Add an English second block for shops whose owners prefer English.
  const en = `\n\n---\nHi! Is this part still in stock?\n${ctx.titleEn}${price ? ` — ${price}` : ""}${shop}${fitEn}\n${ctx.url}`;

  return bn + en;
}


/** Formats a BDT amount for the negotiation message. */
function fmtBdt(n: number): string {
  return `৳${Math.round(n).toLocaleString("en-BD")}`;
}

/**
 * Bilingual buyer→vendor offer message. Kept short so it fits in the
 * ~1000-char `text` limit and reads naturally on a phone.
 */
export function buildOfferMessage(
  ctx: ProductShareContext,
  offerAmount: number,
): string {
  const listed = Number.isFinite(ctx.price) ? fmtBdt(ctx.price) : "";
  const offer = fmtBdt(offerAmount);
  const title = ctx.titleBn ?? ctx.titleEn;
  const bn = `আসসালামু আলাইকুম! এই পার্টটির জন্য আমি ${offer} দিতে চাই।\n${title}${listed ? ` (তালিকা: ${listed})` : ""}\n${ctx.url}`;
  const en = `\n\n---\nHi! I'd like to offer ${offer} for this part.\n${ctx.titleEn}${listed ? ` (listed: ${listed})` : ""}\n${ctx.url}`;
  return bn + en;
}

/**
 * Vendor→buyer counter-offer message. Used when the vendor taps
 * "Counter" in the WhatsApp leads inbox.
 */
export function buildCounterMessage(
  ctx: ProductShareContext,
  counterAmount: number,
  buyerOffer?: number | null,
): string {
  const counter = fmtBdt(counterAmount);
  const buyer = buyerOffer != null && Number.isFinite(buyerOffer) ? fmtBdt(buyerOffer) : null;
  const title = ctx.titleBn ?? ctx.titleEn;
  const bn = `ধন্যবাদ! ${buyer ? `আপনার অফার ${buyer} — ` : ""}আমরা ${counter}-এ দিতে পারি।\n${title}\n${ctx.url}`;
  const en = `\n\n---\nThanks! ${buyer ? `Your offer ${buyer} — ` : ""}we can do ${counter}.\n${ctx.titleEn}\n${ctx.url}`;
  return bn + en;
}

/** Coarse client-side rate check to avoid spammy re-taps. */
const CLICK_MEMO_KEY = "jb.wa.recentClicks";
export function shouldLogWaClick(
  key: string,
  windowMs = 60_000,
  storage: Storage | null = typeof window === "undefined" ? null : window.localStorage,
): boolean {
  if (!storage) return true;
  try {
    const now = Date.now();
    const raw = storage.getItem(CLICK_MEMO_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};
    // Drop expired entries so the object doesn't grow forever.
    for (const k of Object.keys(map)) if (now - map[k] > windowMs) delete map[k];
    if (map[key] && now - map[key] < windowMs) return false;
    map[key] = now;
    storage.setItem(CLICK_MEMO_KEY, JSON.stringify(map));
    return true;
  } catch {
    return true;
  }
}

export type ShopWaContact = {
  phone?: string | null;
  whatsapp_number?: string | null;
  /** ISO timestamp set once the shop confirmed the number by SMS code. */
  whatsapp_verified_at?: string | null;
};

/**
 * Resolve the number a buyer should actually be sent to.
 *
 * An UNVERIFIED `whatsapp_number` is ignored on purpose: a typo there would
 * silently route every lead into a dead chat. Until the shop confirms it by
 * SMS code we keep using the shop phone, which is validated at signup.
 */
export function resolveShopWaTarget(shop: ShopWaContact): {
  number: string | null;
  source: "whatsapp" | "phone" | "none";
} {
  const verified = shop.whatsapp_verified_at ? toWaMeDigits(shop.whatsapp_number) : null;
  if (verified) return { number: verified, source: "whatsapp" };
  const phone = toWaMeDigits(shop.phone);
  if (phone) return { number: phone, source: "phone" };
  return { number: null, source: "none" };
}

/* ------------------------------------------------------------------ *
 * Routing: never hand a buyer a number nobody answers
 * ------------------------------------------------------------------ */

/** Jantro Bazar's own sourcing desk (wa.me digits). */
export const PLATFORM_WA_NUMBER = "8801811600591";

export type WaRoute = {
  /** Who picks up the chat. */
  audience: "shop" | "platform";
  /** wa.me digits — always present. */
  number: string;
  reason: "shop_verified" | "shop_unverified" | "shop_no_number";
};

/**
 * Decide who a buyer's WhatsApp tap should reach.
 *
 * A shop only gets the chat once it has CONFIRMED its own WhatsApp number by
 * SMS code. An unverified number — or a signup phone that may not be on
 * WhatsApp at all, as most seeded shops are — routes to the platform sourcing
 * desk instead, so the buyer always reaches a human.
 */
export function resolveWaRoute(shop: ShopWaContact): WaRoute {
  const target = resolveShopWaTarget(shop);
  if (target.source === "whatsapp" && target.number) {
    return { audience: "shop", number: target.number, reason: "shop_verified" };
  }
  return {
    audience: "platform",
    number: PLATFORM_WA_NUMBER,
    reason: shop.whatsapp_number ? "shop_unverified" : "shop_no_number",
  };
}

/**
 * Buyer→platform sourcing request. Carries the part, the shop it was listed
 * under, and the product link so the desk can chase the right seller.
 */
export function buildSourcingMessage(
  ctx: ProductShareContext & { shopName?: string | null },
): string {
  const title = ctx.titleBn ?? ctx.titleEn;
  const price = Number.isFinite(ctx.price) ? ` (${fmtBdt(ctx.price)})` : "";
  const shop = ctx.shopName ? `\nদোকান: ${ctx.shopName}` : "";
  const shopEn = ctx.shopName ? `\nShop: ${ctx.shopName}` : "";
  const bn = `আসসালামু আলাইকুম যন্ত্র বাজার! এই পার্টটি আমার দরকার:\n${title}${price}${shop}${formatFitment(ctx.fitment, "bn")}\n${ctx.url}`;
  const en = `\n\n---\nHi Jantro Bazar! Please source this part for me:\n${ctx.titleEn}${price}${shopEn}${formatFitment(ctx.fitment, "en")}\n${ctx.url}`;
  return bn + en;
}

/* ------------------------------------------------------------------ *
 * Wave W4 — buyer-facing quiet hours + shop opening line
 * ------------------------------------------------------------------ */

export type ShopQuietHours = {
  enabled: boolean;
  start: string;
  end: string;
  offDays: number[];
};

function hhmm(s: unknown): number | null {
  if (typeof s !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

/** Parse the public `shops.wa_quiet_hours` jsonb into a usable config. */
export function parseShopQuietHours(raw: unknown): ShopQuietHours | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o['enabled'] !== true) return null;
  const start = typeof o['start'] === "string" ? o['start'] : null;
  const end = typeof o['end'] === "string" ? o['end'] : null;
  if (!start || !end || hhmm(start) == null || hhmm(end) == null) return null;
  const offDays = Array.isArray(o['offDays'])
    ? (o['offDays'] as unknown[]).filter((d): d is number => typeof d === "number")
    : [];
  return { enabled: true, start, end, offDays };
}

/** True when `at` falls inside the shop's quiet window / weekly off-day. */
export function isShopQuietAt(cfg: ShopQuietHours | null, at: Date = new Date()): boolean {
  if (!cfg?.enabled) return false;
  if (cfg.offDays.includes(at.getDay())) return true;
  const s = hhmm(cfg.start);
  const e = hhmm(cfg.end);
  if (s == null || e == null || s === e) return false;
  const now = at.getHours() * 60 + at.getMinutes();
  return s < e ? now >= s && now < e : now >= s || now < e;
}

function fmt12h(t: string): string {
  const v = hhmm(t);
  if (v == null) return t;
  const h = Math.floor(v / 60);
  const m = v % 60;
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
}

/**
 * Short, honest note shown under the WhatsApp CTA when the shop is asleep.
 * The message still sends — we only set the buyer's expectation on timing.
 */
export function quietHoursNote(
  cfg: ShopQuietHours | null,
  lang: "en" | "bn",
  at: Date = new Date(),
): string | null {
  if (!isShopQuietAt(cfg, at)) return null;
  const offToday = cfg!.offDays.includes(at.getDay());
  if (offToday) {
    return lang === "bn"
      ? "দোকান আজ বন্ধ — মেসেজ পৌঁছাবে, উত্তর পরের কর্মদিবসে।"
      : "Shop is closed today — your message will arrive, replies come next working day.";
  }
  const back = fmt12h(cfg!.end);
  return lang === "bn"
    ? `দোকান এখন বন্ধ — মেসেজ পৌঁছাবে, উত্তর সাধারণত ${back}-এর পর।`
    : `Shop is closed right now — your message will arrive, replies usually after ${back}.`;
}

/**
 * Prepend the shop's saved opening line (if any) to a pre-filled buyer
 * message so the chat starts the way the vendor asked for.
 */
export function withShopGreeting(
  message: string,
  greeting: string | null | undefined,
): string {
  const g = (greeting ?? "").trim();
  if (!g) return message;
  return `${g.slice(0, 160)}\n\n${message}`;
}
